import { Trans, useLingui } from '@lingui/react/macro';

const isLocaleRequired = (locale: Locale): boolean =>
  (REQUIRED_LOCALES as readonly Locale[]).includes(locale);

import InputText from '@/design_system/InputText';
import { Label } from '@/design_system/Label/Label';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import TextArea from '@/design_system/TextArea';
import { I18nWording, Locale, REQUIRED_LOCALES, SUPPORTED_LOCALES } from '@/services/i18n';

/**
 *
 * @returns two inputs for one text input: one for each available language
 */
export const LocaleInputs = ({
  label,
  placeholder,
  type = 'text-input',
  value,
  setValue,
  showErrors,
  disabled = false,
}: {
  label: string;
  placeholder: string;
  type?: 'text-input' | 'text-area';
  value: I18nWording;
  setValue: (value: I18nWording) => void;
  showErrors: boolean;
  disabled?: boolean;
}) => {
  const { i18n } = useLingui();

  return (
    <>
      {SUPPORTED_LOCALES.map((locale, index) => {
        const localeLabel = i18n._(locale.label);

        if (type === 'text-input') {
          return (
            <InputText
              key={index}
              label={`${label} (${localeLabel})`}
              placeholder={`${placeholder} (${localeLabel})`}
              value={value[locale.id] ?? ''}
              onChange={(newValue) => setValue({ ...value, [locale.id]: newValue })}
              isInvalid={showErrors && isLocaleRequired(locale.id) && !value[locale.id]}
              isDisabled={disabled}
            />
          );
        } else {
          return (
            <TextArea
              key={index}
              label={`${label} (${localeLabel})`}
              placeholder={`${placeholder} (${localeLabel})`}
              value={value[locale.id] ?? ''}
              onChange={(newValue) => {
                setValue({ ...value, [locale.id]: newValue.target.value });
              }}
              isInvalid={showErrors && isLocaleRequired(locale.id) && !value[locale.id]}
              disabled={disabled}
            />
          );
        }
      })}
    </>
  );
};

export const OptionalLocaleInputs = ({
  label,
  placeholder,
  value,
  setValue,
  showErrors,
}: {
  label: string;
  placeholder: string;
  value: I18nWording | null;
  setValue: (value: I18nWording | null) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <>
      <Label label={label}>
        <RadioItemGroup
          name={`default-${label}`}
          value={value === null ? 'default' : 'custom'}
          onChange={(option) => {
            if (option === 'default') {
              setValue(null);
            } else if (option === 'custom') {
              setValue(
                Object.fromEntries(SUPPORTED_LOCALES.map(({ id }) => [id, ''])) as Record<
                  Locale,
                  string
                >
              );
            }
          }}
        >
          <RadioItem
            value="default"
            ariaLabel={t({
              id: 'settings.optional-locale-inputs.default',
              message: 'Default',
            })}
          >
            <Trans id="settings.optional-locale-inputs.default">Default</Trans>
          </RadioItem>
          <RadioItem
            value="custom"
            ariaLabel={t({
              id: 'settings.optional-locale-inputs.custom',
              message: 'Custom',
            })}
          >
            <Trans id="settings.optional-locale-inputs.custom">Custom</Trans>
          </RadioItem>
        </RadioItemGroup>
      </Label>
      {!!value && (
        <LocaleInputs
          label={label}
          placeholder={placeholder}
          value={value}
          setValue={setValue}
          showErrors={showErrors}
        />
      )}
    </>
  );
};
